import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { shallow } from "zustand/shallow";
import toast from "react-hot-toast";
import {
  REALTIME_DOMAINS,
  useRealtime,
  useRealtimeDomain,
} from "@lib/realtime";
import appStore from "@duck/app-store";
import { notifyUserFacingError } from "@util/toErrorMessage";
import { hasPermission, PERMISSIONS } from "@util/permissions";
import { ChatContext } from "./ChatContext";
import * as chatApi from "./chatApi";
import {
  actorKey,
  conversationOnline,
  currentActor,
  isSameActor,
  conversationIdFromAlert,
  conversationIdFromMessage,
  presenceKeyFromPayload,
  unreadTotal,
  upsertById,
} from "./chatHelpers";

const CHAT_CHANNEL = { channel: "ChatChannel" };
const PRESENCE_CHANNEL = { channel: "PresenceChannel" };
const TYPING_HOLD_MS = 3000;

function isChatDenied(err) {
  const message = typeof err === "string" ? err : err?.message;
  return /not allowed to complete this (action|operation)/i.test(message || "");
}

export default function ChatProvider({ children }) {
  const { subscribe, perform } = useRealtime();
  const { admin, user, permissions } = appStore(
    (state) => ({
      admin: state.admin,
      user: state.user,
      permissions: state.permissions,
    }),
    shallow
  );
  const me = useMemo(() => currentActor(admin, user), [admin, user]);
  const canUseChat =
    Boolean(user?.id) || hasPermission(permissions, PERMISSIONS.CHAT_USE);

  const [open, setOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [presence, setPresence] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [typingMap, setTypingMap] = useState({});

  const activeIdRef = useRef(null);
  const openRef = useRef(false);
  activeIdRef.current = activeId;
  openRef.current = open;

  const loadInbox = useCallback((options = {}) => {
    if (!canUseChat) return Promise.resolve();
    const silent = Boolean(options.silent);
    if (!silent) setLoadingInbox(true);
    return chatApi
      .retrieveConversations()
      .then((payload) => {
        const rows = payload?.data || [];
        setConversations(rows);
        const nextPresence = {};
        rows.forEach((conversation) => {
          (conversation.participants || []).forEach((row) => {
            if (row.online) nextPresence[actorKey(row.kind, row.id)] = true;
          });
        });
        setPresence((prev) => ({ ...nextPresence, ...prev }));
      })
      .catch((err) => {
        if (isChatDenied(err) || silent) return;
        notifyUserFacingError(err, toast, { source: "chat_inbox" });
      })
      .finally(() => {
        if (!silent) setLoadingInbox(false);
      });
  }, [canUseChat, me]);

  const loadContacts = useCallback(() => {
    if (!canUseChat) return Promise.resolve();
    return chatApi
      .retrieveChatContacts()
      .then((payload) => {
        const rows = payload?.data || [];
        setContacts(rows);
        setPresence((prev) => {
          const next = { ...prev };
          rows.forEach((row) => {
            if (row.online) next[actorKey(row.kind, row.id)] = true;
          });
          return next;
        });
      })
      .catch((err) => {
        if (isChatDenied(err)) return;
        notifyUserFacingError(err, toast, { source: "chat_contacts" });
      });
  }, [canUseChat]);

  const loadThread = useCallback((conversationId, options = {}) => {
    if (!conversationId) return Promise.resolve();
    const silent = Boolean(options.silent);
    if (!silent) setLoadingThread(true);
    return Promise.all([
      chatApi.retrieveConversation(conversationId),
      chatApi.retrieveMessages(conversationId),
    ])
      .then(([conversation, messagePayload]) => {
        setActiveConversation(conversation);
        setMessages(messagePayload?.data || []);
        setConversations((prev) => upsertById(prev, conversation));
      })
      .catch((err) => {
        if (!silent) notifyUserFacingError(err, toast, { source: "chat_thread" });
      })
      .finally(() => {
        if (!silent) setLoadingThread(false);
      });
  }, [me]);

  const markRead = useCallback((conversationId) => {
    if (!conversationId) return Promise.resolve();
    return chatApi
      .markConversationRead(conversationId)
      .then((conversation) => {
        if (conversation) {
          setConversations((prev) => upsertById(prev, conversation));
          setActiveConversation((prev) =>
            prev && String(prev.id) === String(conversation.id)
              ? { ...prev, ...conversation }
              : prev
          );
        }
      })
      .catch(() => {});
  }, []);

  const openTray = useCallback(() => {
    setOpen(true);
    loadInbox();
  }, [loadInbox]);

  const closeTray = useCallback(() => {
    setOpen(false);
    setNewOpen(false);
  }, []);

  const toggleTray = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) loadInbox();
      return next;
    });
  }, [loadInbox]);

  const openConversation = useCallback(
    (conversationId) => {
      if (!conversationId) return;
      setOpen(true);
      setNewOpen(false);
      setActiveId(conversationId);
      loadThread(conversationId).then(() => markRead(conversationId));
    },
    [loadThread, markRead]
  );

  const startDirect = useCallback(
    (contact) => {
      const payload =
        contact.kind === "admin"
          ? { variety: 1, admin_id: contact.id }
          : { variety: 1, user_id: contact.id };
      return chatApi
        .createConversation(payload)
        .then((conversation) => {
          setConversations((prev) => upsertById(prev, conversation));
          openConversation(conversation.id);
          return conversation;
        })
        .catch((err) => {
          notifyUserFacingError(err, toast, { source: "chat_create" });
          throw err;
        });
    },
    [openConversation]
  );

  const startGroup = useCallback(
    (name, selectedContacts) => {
      return chatApi
        .createConversation({
          variety: 2,
          name,
          participants: selectedContacts.map((contact) =>
            contact.kind === "admin"
              ? { admin_id: contact.id }
              : { user_id: contact.id }
          ),
        })
        .then((conversation) => {
          setConversations((prev) => upsertById(prev, conversation));
          openConversation(conversation.id);
          return conversation;
        })
        .catch((err) => {
          notifyUserFacingError(err, toast, { source: "chat_create" });
          throw err;
        });
    },
    [openConversation]
  );

  const sendMessage = useCallback(
    (body, files = []) => {
      if (!activeId) return Promise.resolve();
      const trimmed = (body || "").trim();
      if (!trimmed && (!files || !files.length)) return Promise.resolve();
      setSending(true);
      const uploads = (files || []).map((file) =>
        chatApi.uploadChatAsset(file, me)
      );
      return Promise.all(uploads)
        .then((assets) => {
          const assetIds = assets.map((asset) => asset.id).filter(Boolean);
          return chatApi.createMessage(activeId, {
            body: trimmed,
            asset_ids: assetIds,
          });
        })
        .then((message) => {
          if (message?.id) {
            setMessages((prev) => {
              if (prev.some((row) => String(row.id) === String(message.id))) {
                return prev;
              }
              return [...prev, message];
            });
          }
          loadInbox();
        })
        .catch((err) => notifyUserFacingError(err, toast, { source: "chat_send" }))
        .finally(() => setSending(false));
    },
    [activeId, loadInbox, me]
  );

  const addPeople = useCallback(
    (contact) => {
      if (!activeId || !contact) return Promise.resolve();
      const payload =
        contact.kind === "admin"
          ? { admin_id: contact.id }
          : { user_id: contact.id };
      return chatApi
        .addConversationParticipant(activeId, payload)
        .then((conversation) => {
          setActiveConversation(conversation);
          setConversations((prev) => upsertById(prev, conversation));
        })
        .catch((err) => notifyUserFacingError(err, toast, { source: "chat_add" }));
    },
    [activeId]
  );

  const notifyTyping = useCallback(() => {
    if (!activeId) return;
    perform(CHAT_CHANNEL, "typing", { conversation_id: activeId });
  }, [activeId, perform]);

  useEffect(() => {
    const ids = Object.keys(typingMap);
    if (!ids.length) return undefined;
    const nextExpiry = Math.min(
      ...ids.map((id) => Number(typingMap[id]?.expiresAt) || 0)
    );
    const wait = Math.max(50, nextExpiry - Date.now());
    const timer = setTimeout(() => {
      const now = Date.now();
      setTypingMap((prev) => {
        const next = {};
        Object.keys(prev).forEach((id) => {
          if (Number(prev[id]?.expiresAt) > now) next[id] = prev[id];
        });
        return next;
      });
    }, wait);
    return () => clearTimeout(timer);
  }, [typingMap]);

  const leaveActive = useCallback(() => {
    if (!activeId) return Promise.resolve();
    const leavingId = activeId;
    return chatApi
      .leaveConversation(leavingId)
      .then(() => {
        setConversations((prev) =>
          prev.filter((row) => String(row.id) !== String(leavingId))
        );
        setActiveId(null);
        setActiveConversation(null);
        setMessages([]);
      })
      .catch((err) => notifyUserFacingError(err, toast, { source: "chat_leave" }));
  }, [activeId]);

  useEffect(() => {
    if (!canUseChat) return undefined;
    return subscribe(CHAT_CHANNEL);
  }, [canUseChat, subscribe]);
  useEffect(() => {
    if (!canUseChat) return undefined;
    return subscribe(PRESENCE_CHANNEL);
  }, [canUseChat, subscribe]);
  useEffect(() => {
    if (!canUseChat) return;
    loadInbox();
    loadContacts();
  }, [canUseChat, loadContacts, loadInbox]);

  const refreshConversation = useCallback(
    (conversationId) => {
      loadInbox({ silent: true });
      if (
        conversationId &&
        String(activeIdRef.current) === String(conversationId)
      ) {
        loadThread(conversationId, { silent: true });
        if (openRef.current) markRead(conversationId);
      }
    },
    [loadInbox, loadThread, markRead]
  );

  useRealtimeDomain(REALTIME_DOMAINS.CHAT, (event) => {
    const payload = event.payload;
    const type = String(event.type || "").replace(/\./g, "_");
    if (type === "conversation_created" && payload?.id) {
      setConversations((prev) => upsertById(prev, payload));
      return;
    }
    if (type === "conversation_participant_added" && payload?.id) {
      setConversations((prev) => upsertById(prev, payload));
      if (String(activeIdRef.current) === String(payload.id)) {
        setActiveConversation(payload);
      }
      return;
    }
    if (type === "conversation_read" && payload?.id) {
      setConversations((prev) => upsertById(prev, payload));
      return;
    }
    if (type === "conversation_typing" && payload?.conversation_id) {
      if (isSameActor(payload, me)) return;
      setTypingMap((prev) => ({
        ...prev,
        [payload.conversation_id]: {
          kind: payload.kind,
          id: payload.id,
          expiresAt: Date.now() + TYPING_HOLD_MS,
        },
      }));
      return;
    }
    if (type === "message_created" && payload?.id) {
      const conversationId = conversationIdFromMessage(payload);
      if (conversationId) {
        setTypingMap((prev) => {
          if (!prev[conversationId]) return prev;
          const next = { ...prev };
          delete next[conversationId];
          return next;
        });
        if (String(activeIdRef.current) === String(conversationId)) {
          setMessages((prev) => {
            if (prev.some((row) => String(row.id) === String(payload.id))) {
              return prev;
            }
            return [...prev, payload];
          });
          if (openRef.current) markRead(conversationId);
        }
        setConversations((prev) => {
          const existing = (prev || []).find(
            (row) => String(row.id) === String(conversationId)
          );
          if (!existing) {
            loadInbox({ silent: true });
            return prev;
          }
          const fromMe = isSameActor(payload.sender, me);
          const viewing =
            openRef.current &&
            String(activeIdRef.current) === String(conversationId);
          return upsertById(prev, {
            ...existing,
            last_message: payload,
            last_message_at: payload.created_at,
            unread_count: viewing || fromMe
              ? existing.unread_count
              : (Number(existing.unread_count) || 0) + 1,
          });
        });
      }
    }
  });

  useRealtimeDomain(REALTIME_DOMAINS.ALERT, (event) => {
    const type = String(event.type || "").replace(/\./g, "_");
    if (type !== "alert_created") return;
    const conversationId = conversationIdFromAlert(event.payload);
    if (conversationId) refreshConversation(conversationId);
  });

  useRealtimeDomain(REALTIME_DOMAINS.PRESENCE, (event) => {
    const key = presenceKeyFromPayload(event.payload);
    if (!key) return;
    const type = String(event.type || "").replace(/\./g, "_");
    if (type === "presence_online") {
      setPresence((prev) => ({ ...prev, [key]: true }));
    }
    if (type === "presence_offline") {
      setPresence((prev) => ({ ...prev, [key]: false }));
    }
  });

  const value = useMemo(
    () => ({
      me,
      open,
      newOpen,
      setNewOpen,
      conversations,
      contacts,
      presence,
      activeId,
      activeConversation,
      messages,
      loadingInbox,
      loadingThread,
      sending,
      typingMap,
      unreadCount: unreadTotal(conversations),
      conversationIsOnline: (conversation) =>
        conversationOnline(conversation, me, presence),
      openTray,
      closeTray,
      toggleTray,
      openConversation,
      startDirect,
      startGroup,
      sendMessage,
      notifyTyping,
      addPeople,
      leaveActive,
      loadContacts,
    }),
    [
      activeConversation,
      activeId,
      addPeople,
      closeTray,
      contacts,
      conversations,
      leaveActive,
      loadContacts,
      loadingInbox,
      loadingThread,
      me,
      messages,
      newOpen,
      notifyTyping,
      open,
      openConversation,
      openTray,
      presence,
      sendMessage,
      sending,
      startDirect,
      startGroup,
      toggleTray,
      typingMap,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
